/**
 * Token Storage Utility
 * Handles secure storage of access and refresh tokens
 */

class TokenStorage {
  constructor() {
    this.ACCESS_TOKEN_KEY = 'accessToken';
    this.REFRESH_TOKEN_KEY = 'refreshToken';
    this.TOKEN_EXPIRY_KEY = 'tokenExpiry';
  }

  /**
   * Set access token with 24-hour expiration
   * @param {string} token - JWT access token
   */
  setAccessToken(token) {
    try {
      // Store in localStorage to persist across browser restarts
      localStorage.setItem(this.ACCESS_TOKEN_KEY, token);

      // Calculate expiry time (24 hours from now)
      const expiryTime = Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());

      // Also set HTTP-only cookie for server-side access
      this.setCookie(this.ACCESS_TOKEN_KEY, token, 24); // 24 hours

      // Clear token cache in authSlice
      if (typeof window !== 'undefined' && window.clearTokenCache) {
        window.clearTokenCache();
      }
    } catch (error) {
      console.error('Error storing access token:', error);
    }
  }

  /**
   * Set refresh token without expiration
   * @param {string} token - JWT refresh token
   */
  setRefreshToken(token) {
    try {
      // Store in localStorage (persists across restarts)
      localStorage.setItem(this.REFRESH_TOKEN_KEY, token);

      // Also set HTTP-only cookie for server-side access
      this.setCookie(this.REFRESH_TOKEN_KEY, token, 30 * 24); // 30 days
    } catch (error) {
      console.error('Error storing refresh token:', error);
    }
  }

  /**
   * Get access token
   * @returns {string|null} - Access token or null
   */
  getAccessToken() {
    try {
      const token = localStorage.getItem(this.ACCESS_TOKEN_KEY);
      return token;
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  }

  /**
   * Get refresh token
   * @returns {string|null} - Refresh token or null
   */
  getRefreshToken() {
    try {
      const token = localStorage.getItem(this.REFRESH_TOKEN_KEY);
      return token;
    } catch (error) {
      console.error('Error getting refresh token:', error);
      return null;
    }
  }

  /**
   * Check if access token is expired
   * @returns {boolean} - True if expired
   */
  isAccessTokenExpired() {
    try {
      const token = this.getAccessToken();
      if (!token) return true;

      // Check stored expiry time
      const storedExpiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
      if (storedExpiry) {
        const expiryTime = parseInt(storedExpiry);
        const now = Date.now();
        return now >= expiryTime;
      }

      // Fallback: parse JWT token
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp < now;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }

  /**
   * Check if access token expires soon (within 1 hour)
   * @returns {boolean} - True if expires soon
   */
  isAccessTokenExpiringSoon() {
    try {
      const token = this.getAccessToken();
      if (!token) return true;

      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = payload.exp - now;

      // Expires within 1 hour
      return timeUntilExpiry < 60 * 60;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }

  /**
   * Clear all tokens
   */
  clearTokens() {
    try {
      localStorage.removeItem(this.ACCESS_TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem(this.TOKEN_EXPIRY_KEY);

      // Clear cookies
      this.clearCookie(this.ACCESS_TOKEN_KEY);
      this.clearCookie(this.REFRESH_TOKEN_KEY);

      // Clear token cache in authSlice
      if (typeof window !== 'undefined' && window.clearTokenCache) {
        window.clearTokenCache();
      }
    } catch (error) {
      console.error('Error clearing tokens:', error);
    }
  }

  /**
   * Replace old refresh token with new one
   * @param {string} newRefreshToken - New refresh token
   */
  replaceRefreshToken(newRefreshToken) {
    try {
      // Remove old refresh token
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      this.clearCookie(this.REFRESH_TOKEN_KEY);

      // Set new refresh token
      this.setRefreshToken(newRefreshToken);
    } catch (error) {
      console.error('Error replacing refresh token:', error);
    }
  }

  /**
   * Set HTTP-only cookie
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} hours - Expiration in hours
   */
  setCookie(name, value, hours) {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + hours * 60 * 60 * 1000);

      // Note: In a real implementation, this would be set by the server
      // For now, we'll use a regular cookie as fallback
      document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; secure; samesite=strict`;
    } catch (error) {
      console.error('Error setting cookie:', error);
    }
  }

  /**
   * Clear cookie
   * @param {string} name - Cookie name
   */
  clearCookie(name) {
    try {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    } catch (error) {
      console.error('Error clearing cookie:', error);
    }
  }

  /**
   * Get token info for debugging
   * @returns {object} - Token information
   */
  getTokenInfo() {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    let accessTokenInfo = null;
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        accessTokenInfo = {
          expires: new Date(payload.exp * 1000),
          timeUntilExpiry: Math.floor((payload.exp - Date.now() / 1000) / 60), // minutes
          isExpired: this.isAccessTokenExpired(),
          isExpiringSoon: this.isAccessTokenExpiringSoon(),
        };
      } catch (error) {
        accessTokenInfo = { error: 'Invalid token format' };
      }
    }

    return {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      accessTokenInfo,
    };
  }
}

// Create singleton instance
const tokenStorage = new TokenStorage();

export default tokenStorage;
